import { useCallback, useEffect, useState } from "react";

import {
  AiCostPlaneValue,
  AiCostSummary,
  AiReportRange,
  aiGatewayService,
} from "services/aiGatewayService";

/**
 * Loads the AI Gateway spend summary for a window and a plane.
 *
 * It is deliberately **not** part of `useAiGateway`: the summary aggregates the largest table a
 * tenant has, and the gateway page loads on every visit to Connectors or Routing. Keeping it here
 * means the query only runs once the Costs tab is actually opened, and re-runs when the window or
 * the plane changes rather than when an unrelated connector is saved.
 *
 * `days` is clamped to 1–90 server-side and echoed back on the result — read the window off
 * `summary.days`, not off what was asked for.
 *
 * `refreshToken` is what the page's Refresh button changes: the panel keeps its filters and the
 * query re-runs, rather than the panel remounting and snapping back to the default window.
 *
 * The axios interceptor already fires an error toast on failure; `error` here only drives the
 * inline empty/error state.
 */
export function useAiCosts(
  days: number,
  plane: AiCostPlaneValue,
  refreshToken = 0,
  range?: AiReportRange,
) {
  const startDate = range?.startDate ?? null;
  const endDate = range?.endDate ?? null;
  const [summary, setSummary] = useState<AiCostSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const loaded = await aiGatewayService.getCostSummary(
        days,
        plane,
        startDate && endDate ? { startDate, endDate } : undefined,
      );
      setSummary(loaded);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "load-failed");
    } finally {
      setLoading(false);
    }
    // `refreshToken` is not read here — it is the dependency that re-runs the same query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, plane, startDate, endDate, refreshToken]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { summary, loading, error, reload };
}
